// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Page } from '@/hooks/usePages';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  Search, 
  Calendar, 
  Trophy, 
  Award, 
  QrCode, 
  Mail, 
  ChevronRight,
  Star,
  Target,
  BarChart3,
  Zap,
  Users,
  BookOpen,
  CreditCard,
  Sparkles,
  ArrowRight
} from 'lucide-react';

const DynamicPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<Page | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Handle root path - if no slug, we're on the home page
        const pageSlug = slug === undefined ? '/' : `/${slug}`;
        
        console.log('Fetching page with slug:', pageSlug);
        
        const { data, error } = await supabase
          .from('pages')
          .select('*')
          .eq('slug', pageSlug)
          .eq('is_published', true)
          .maybeSingle();

        if (error) {
          console.error('Error fetching page:', error);
          throw error;
        }
        
        console.log('Page data found:', data);
        
        if (!data) {
          // For the home page, show our custom home page instead of error
          if (pageSlug === '/') {
            setPage(null); // This will trigger our custom home page
          } else {
            setError('Page not found');
          }
        } else {
          setPage(data);
        }
      } catch (err) {
        console.error('Error fetching page:', err);
        setError('Failed to load page');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // If we have page data from database, render it
  if (page) {
    return (
      <div className="w-full">
        <div 
          className="w-full"
          dangerouslySetInnerHTML={{ __html: page.content || '' }}
        />
      </div>
    );
  }

  // Custom home page design for when no database content exists
  return (
    <div className="min-h-screen w-full overflow-hidden">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-[#002E62] via-[#003875] to-[#004488]">
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
        <div 
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
        
        <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <div className="mb-8">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6 border border-white/20">
              <Sparkles className="h-4 w-4 mr-2" />
              The Future of Athletic Recruiting
            </div>
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold mb-6 leading-tight">
              Built for Athletes.
              <span className="block bg-gradient-to-r from-[#BC8D09] to-[#FFD700] bg-clip-text text-transparent">
                Powered by Innovation.
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl lg:text-3xl mb-12 leading-relaxed font-light opacity-90">
              Level up your recruiting journey — with RecruitLook.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <Link 
              to="/login" 
              className="group bg-gradient-to-r from-[#BC8D09] to-[#FFD700] hover:from-[#9A7208] hover:to-[#E6C200] text-white px-10 py-5 text-lg font-semibold rounded-xl transition-all duration-300 shadow-2xl hover:shadow-[#BC8D09]/25 hover:scale-105 inline-flex items-center justify-center min-w-[200px]"
            >
              Sign In
              <ArrowRight className="ml-3 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <Link 
              to="/auth/login" 
              className="group border-2 border-white/30 text-white hover:bg-white hover:text-[#002E62] px-10 py-5 text-lg font-semibold rounded-xl transition-all duration-300 backdrop-blur-sm hover:backdrop-blur-none inline-flex items-center justify-center min-w-[200px]"
            >
              Sign In
              <ChevronRight className="ml-3 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">10K+</div>
              <div className="text-white/80 font-medium">Student Athletes</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">2.5K+</div>
              <div className="text-white/80 font-medium">College Programs</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">95%</div>
              <div className="text-white/80 font-medium">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">50+</div>
              <div className="text-white/80 font-medium">Sports Covered</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center bg-[#002E62]/10 text-[#002E62] px-6 py-3 rounded-full text-sm font-medium mb-6">
              <Trophy className="h-4 w-4 mr-2" />
              Complete Recruiting Platform
            </div>
            <h2 className="text-5xl md:text-6xl font-heading font-bold text-gray-900 mb-8">
              Everything You Need
              <span className="block text-[#002E62]">To Get Recruited</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our comprehensive suite of tools helps student-athletes navigate the recruiting process with confidence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: User,
                title: "Complete Athlete Profile",
                description: "Showcase your athletic achievements, academic performance, and personal brand to college coaches.",
                color: "bg-blue-500",
                bgGradient: "from-blue-50 to-blue-100"
              },
              {
                icon: Search,
                title: "Smart College Search",
                description: "Find your perfect college match with intelligent search filters and recommendations.",
                color: "bg-green-500",
                bgGradient: "from-green-50 to-green-100"
              },
              {
                icon: Mail,
                title: "Email Tools",
                description: "Send personalized emails to college coaches with professional templates and tracking.",
                color: "bg-purple-500",
                bgGradient: "from-purple-50 to-purple-100"
              },
              {
                icon: Target,
                title: "Recruiting Tracker",
                description: "Stay organized and track every interaction with college programs throughout your journey.",
                color: "bg-amber-500",
                bgGradient: "from-amber-50 to-amber-100"
              },
              {
                icon: Calendar,
                title: "Recruiting Calendar",
                description: "Never miss important recruiting deadlines and dates specific to your sport and grade level.",
                color: "bg-red-500",
                bgGradient: "from-red-50 to-red-100"
              },
              {
                icon: Award,
                title: "Scholarship Finder",
                description: "Discover athletic and academic scholarship opportunities that match your profile.",
                color: "bg-pink-500",
                bgGradient: "from-pink-50 to-pink-100"
              }
            ].map((feature, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-500 border-0 shadow-lg hover:scale-105">
                <CardContent className="p-8">
                  <div className={`bg-gradient-to-r ${feature.bgGradient} rounded-2xl p-8 mb-6`}>
                    <div className={`${feature.color} rounded-xl p-4 inline-flex mb-4`}>
                      <feature.icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">{feature.description}</p>
                    <Link 
                      to="/login" 
                      className="text-[#002E62] hover:text-[#001A3A] font-semibold inline-flex items-center group-hover:translate-x-2 transition-transform"
                    >
                      Sign In
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="py-32 bg-gradient-to-r from-[#002E62] via-[#003875] to-[#002E62] relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
        
        <div className="relative z-10 max-w-5xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-5xl md:text-6xl font-heading font-bold text-white mb-8">
            Ready to Dominate
            <span className="block bg-gradient-to-r from-[#BC8D09] to-[#FFD700] bg-clip-text text-transparent">
              Your Recruiting?
            </span>
          </h2>
          
          <p className="text-xl md:text-2xl text-white/90 mb-12 leading-relaxed max-w-3xl mx-auto">
            Join thousands of student-athletes who are using RecruitLook to connect with college coaches 
            and secure their athletic future.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link 
              to="/login" 
              className="group bg-gradient-to-r from-[#BC8D09] to-[#FFD700] hover:from-[#9A7208] hover:to-[#E6C200] text-white px-12 py-6 text-xl font-semibold rounded-xl transition-all duration-300 shadow-2xl hover:shadow-[#BC8D09]/25 hover:scale-105 inline-flex items-center justify-center min-w-[250px]"
            >
              Sign In
              <Zap className="ml-3 h-6 w-6 group-hover:rotate-12 transition-transform" />
            </Link>
            
            <Link 
              to="/about" 
              className="group border-2 border-white/30 text-white hover:bg-white hover:text-[#002E62] px-12 py-6 text-xl font-semibold rounded-xl transition-all duration-300 backdrop-blur-sm hover:backdrop-blur-none inline-flex items-center justify-center min-w-[250px]"
            >
              Learn More
              <BookOpen className="ml-3 h-6 w-6 group-hover:scale-110 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicPage;
